import { ServiceType } from 'types/generated/graphql';

export type PhoneNumbers = {
  phoneNumber?: string;
  senderPhoneNumber?: string;
};

/**
 * Minimal structural shape of a customer PIN credential needed to derive contact info.
 * Kept loose on purpose so both the detail (AllocationDto) and list (StorageUnitList)
 * fragments satisfy it.
 */
export type ContactCredential = {
  phone?: string | null;
  email?: string | null;
  pickupAction: boolean;
  storeAction: boolean;
};

/**
 * Extracts phone numbers from customer PIN credentials based on service type
 *
 * @param credentials - Array of customer PIN credentials
 * @param serviceType - Type of service (Delivery, BagToBox, HandToHand)
 * @returns Object containing phoneNumber (recipient) and senderPhoneNumber
 *
 * Service type rules:
 * - Delivery: Single phone number from available credential (recipient)
 * - Bag to Box: Single phone number from available credential
 * - Hand to Hand:
 *   - Recipient = pickupAction=true only (storeAction != true)
 *   - Sender = storeAction=true + pickupAction=true
 */
export const getPhoneNumbers = (
  credentials: ReadonlyArray<ContactCredential> | undefined | null,
  serviceType: ServiceType | undefined | null
): PhoneNumbers => {
  if (!credentials || !serviceType) {
    return {};
  }

  if (
    serviceType === ServiceType.Delivery ||
    serviceType === ServiceType.BagToBox
  ) {
    // Delivery / Bag to Box: single recipient credential, show its phone
    return {
      phoneNumber: credentials[0]?.phone ?? undefined,
    };
  }

  if (serviceType === ServiceType.HandToHand) {
    // Hand to Hand:
    // recipient = pickupAction=true only (storeAction=false or undefined)
    // sender = storeAction=true + pickupAction=true
    const recipientCredentials = credentials.find(
      (cred) => cred.pickupAction === true && cred.storeAction !== true
    );
    const senderCredentials = credentials.find(
      (cred) => cred.storeAction === true && cred.pickupAction === true
    );

    return {
      phoneNumber: recipientCredentials?.phone ?? undefined,
      senderPhoneNumber: senderCredentials?.phone ?? undefined,
    };
  }

  return {};
};
